import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrackChanges as GoalIcon,
  Business as ProfessionalIcon,
  Person as PersonalIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { apiUrl } from '../../config/api';
import axios from 'axios';

export interface Goal {
  id: number;
  member_id: number;
  team_id: number;
  type: 'professional' | 'personal';
  title: string;
  description: string;
  timeline: string;
  priority: number;
  skills_needed: string;
  member_name: string;
  member_role: string;
  user_email: string;
  created_at: string;
  updated_at: string;
}

interface GoalFormData {
  member_id: number | '';
  type: 'professional' | 'personal';
  title: string;
  description: string;
  timeline: string;
  priority: number;
  skills_needed: string;
}

interface TeamMember {
  id: number;
  user_name: string;
  user_email: string;
  role: string;
  skills?: string;
}

interface GoalsProps {
  onGoalsChange?: (goals: Goal[]) => void;
}

const Goals: React.FC<GoalsProps> = ({ onGoalsChange }) => {
  const { user, currentTeam } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [filterMemberId, setFilterMemberId] = useState<number | 'all'>('all');
  const [formData, setFormData] = useState<GoalFormData>({
    member_id: '',
    type: 'professional',
    title: '',
    description: '',
    timeline: '3 years',
    priority: 3,
    skills_needed: ''
  });

  const fetchGoals = React.useCallback(async () => {
    try {
      setLoading(true);
      if (!user || !currentTeam) {
        setError('Please select a team');
        setGoals([]);
        return;
      }

      const response = await axios.get(apiUrl('/api/goals'));
      setGoals(response.data);
      onGoalsChange?.(response.data);
    } catch (err) {
      setError('Failed to load goals');
      console.error('Error fetching goals:', err);
    } finally {
      setLoading(false);
    }
  }, [user, currentTeam, onGoalsChange]);

  const fetchMembers = React.useCallback(async () => {
    try {
      if (!currentTeam) return;
      
      const response = await axios.get(apiUrl(`/api/team-members/team/${currentTeam.id}`));
      setMembers(response.data);
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  }, [currentTeam]);

  useEffect(() => {
    if (currentTeam) {
      fetchGoals();
      fetchMembers();
    }
  }, [fetchGoals, fetchMembers, currentTeam]);

  const handleAddGoal = () => {
    setEditingGoal(null);
    
    // For regular users, automatically set their own member_id
    const defaultMemberId = !isAdmin 
      ? members.find(member => member.user_email === user?.email)?.id || ''
      : '';
    
    setFormData({
      member_id: defaultMemberId,
      type: 'professional',
      title: '',
      description: '',
      timeline: '3 years',
      priority: 3,
      skills_needed: ''
    });
    setDialogOpen(true);
  };

  const handleEditGoal = (goal: Goal) => {
    setEditingGoal(goal);
    setFormData({
      member_id: goal.member_id,
      type: goal.type,
      title: goal.title,
      description: goal.description,
      timeline: goal.timeline,
      priority: goal.priority,
      skills_needed: goal.skills_needed
    });
    setDialogOpen(true);
  };

  const handleDeleteGoal = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      try {
        await axios.delete(apiUrl(`/api/goals/${id}`));
        fetchGoals();
      } catch (err) {
        setError('Failed to delete goal');
        console.error('Error deleting goal:', err);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingGoal) {
        await axios.put(apiUrl(`/api/goals/${editingGoal.id}`), formData);
      } else {
        await axios.post(apiUrl('/api/goals'), formData);
      }
      setDialogOpen(false);
      fetchGoals();
    } catch (err) {
      setError('Failed to save goal');
      console.error('Error saving goal:', err);
    }
  };

  const handleFormChange = (field: keyof GoalFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getPriorityColor = (priority: number) => {
    if (priority >= 4) return 'error';
    if (priority >= 3) return 'warning';
    return 'info';
  };

  const getPriorityLabel = (priority: number) => {
    if (priority >= 4) return 'High';
    if (priority >= 3) return 'Medium';
    return 'Low';
  };

  if (!currentTeam) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">
          Please select a team to view and manage goals.
        </Alert>
      </Container>
    );
  }

  const isAdmin = currentTeam.role === 'admin' || currentTeam.role === 'manager';

  // Filter goals by selected team member (frontend only)
  const filteredGoals = filterMemberId === 'all' 
    ? goals 
    : goals.filter(goal => goal.member_id === filterMemberId);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Goals Management
          </Typography>
          {isAdmin ? (
            <Chip 
              label="Admin View - All Team Goals" 
              color="primary" 
              size="small"
              sx={{ mr: 1 }}
            />
          ) : (
            <Chip 
              label="Personal View - Your Goals Only" 
              color="default" 
              size="small"
              sx={{ mr: 1 }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Member</InputLabel>
              <Select
                value={filterMemberId}
                onChange={(e) => setFilterMemberId(e.target.value as number | 'all')}
                label="Filter by Member"
              >
                <MenuItem value="all">All Members</MenuItem>
                {members.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.user_name} ({member.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddGoal}
          >
            Add Goal
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {filteredGoals.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <GoalIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {goals.length === 0 
              ? 'No goals defined yet'
              : filterMemberId !== 'all'
              ? `No goals found for selected team member`
              : 'No goals defined yet'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {goals.length === 0 
              ? 'Start by adding professional and personal development goals for team members. Goals help identify collaboration opportunities and skill synergies.'
              : 'Try selecting a different team member or clear the filter.'}
          </Typography>
          {goals.length === 0 && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddGoal}>
              Add First Goal
            </Button>
          )}
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {filteredGoals.map((goal) => (
            <Box key={goal.id} sx={{ flex: '1 1 400px', minWidth: '300px' }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {goal.type === 'professional' ? (
                      <ProfessionalIcon color="primary" sx={{ mr: 1 }} />
                    ) : (
                      <PersonalIcon color="secondary" sx={{ mr: 1 }} />
                    )}
                    <Typography variant="h6" component="div">
                      {goal.title}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {goal.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      label={goal.type} 
                      color={goal.type === 'professional' ? 'primary' : 'secondary'}
                      size="small"
                    />
                    <Chip 
                      label={`Priority: ${getPriorityLabel(goal.priority)}`} 
                      color={getPriorityColor(goal.priority)}
                      size="small"
                    />
                    <Chip 
                      label={goal.timeline} 
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  
                  {goal.skills_needed && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Skills needed:
                      </Typography>
                      <Typography variant="body2">
                        {goal.skills_needed}
                      </Typography>
                    </Box>
                  )}
                  
                  <Typography variant="caption" color="text.secondary">
                    Owner: {goal.member_name} ({goal.member_role})
                  </Typography>
                </CardContent>
                
                <CardActions>
                  <IconButton 
                    size="small" 
                    onClick={() => handleEditGoal(goal)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDeleteGoal(goal.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingGoal ? 'Edit Goal' : 'Add New Goal'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Team Member</InputLabel>
              <Select
                value={formData.member_id}
                onChange={(e) => handleFormChange('member_id', e.target.value)}
                label="Team Member"
                disabled={!isAdmin}
              >
                {isAdmin ? (
                  members.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.user_name} ({member.role})
                    </MenuItem>
                  ))
                ) : (
                  // Regular users can only select themselves
                  members.filter(member => member.user_email === user?.email).map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.user_name} ({member.role}) - You
                    </MenuItem>
                  ))
                )}
              </Select>
              {!isAdmin && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  You can only add goals for yourself
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Goal Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleFormChange('type', e.target.value)}
                label="Goal Type"
              >
                <MenuItem value="professional">Professional Development</MenuItem>
                <MenuItem value="personal">Personal Development</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Goal Title"
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              fullWidth
              required
              placeholder="e.g., Become a Senior Technical Lead"
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe the goal in detail, what success looks like, and why it's important"
            />

            <FormControl fullWidth>
              <InputLabel>Timeline</InputLabel>
              <Select
                value={formData.timeline}
                onChange={(e) => handleFormChange('timeline', e.target.value)}
                label="Timeline"
              >
                <MenuItem value="6 months">6 months</MenuItem>
                <MenuItem value="1 year">1 year</MenuItem>
                <MenuItem value="2 years">2 years</MenuItem>
                <MenuItem value="3 years">3 years</MenuItem>
                <MenuItem value="5+ years">5+ years</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => handleFormChange('priority', e.target.value)}
                label="Priority"
              >
                <MenuItem value={1}>1 - Low</MenuItem>
                <MenuItem value={2}>2 - Medium-Low</MenuItem>
                <MenuItem value={3}>3 - Medium</MenuItem>
                <MenuItem value={4}>4 - High</MenuItem>
                <MenuItem value={5}>5 - Critical</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Skills Needed"
              value={formData.skills_needed}
              onChange={(e) => handleFormChange('skills_needed', e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="List skills, knowledge, or experiences needed (comma-separated)"
              helperText="e.g., React, Leadership, Public Speaking, Project Management"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.member_id || !formData.title}
          >
            {editingGoal ? 'Update' : 'Add'} Goal
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Goals;